<script lang="ts" setup>
import {computed, ref} from "vue";
import {storeToRefs} from "pinia";
import {useSessionStore} from "@/stores/session-store";
import {useAlertStore} from "@/stores/alerts-store";
import {useOrgUsers, useAdminRemoveUserFromOrg, useResendInvite, useRevokeInvite} from "@/queries/orgs";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import InviteUserModal from "@/components/InviteUserModal.vue";
import AddUserModal from "@/components/AddUserModal.vue";
import EditUserModal from "@/components/EditUserModal.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import type {IUser} from "@/types";

import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import type {Config} from "datatables.net";

const sessionStore = useSessionStore();
const alertStore = useAlertStore();
const { session } = storeToRefs(sessionStore);
const { orgUsers, orgUsersLoading, refetchOrgUsers } = useOrgUsers();
const { adminRemoveUserFromOrg } = useAdminRemoveUserFromOrg();
const { resendInvite } = useResendInvite();
const { revokeInvite } = useRevokeInvite();

const showInviteModal = ref(false);
const showAddUserModal = ref(false);
const showEditUserModal = ref(false);
const showDeleteModal = ref(false);
const selectedUser = ref<IUser | null>(null);
const userToDelete = ref<{ id: string; name: string } | null>(null);

const canManageUsers = computed(() =>
  session.value.isSuperAdmin || session.value.role === "OrgAdmin"
);

const openInviteModal = () => { showInviteModal.value = true; };
const openAddUserModal = () => { showAddUserModal.value = true; };

const openEditUserModal = (user: IUser) => {
  selectedUser.value = user;
  showEditUserModal.value = true;
};

const handleUserInvited = () => {
  showInviteModal.value = false;
  refetchOrgUsers();
};

const handleUserAdded = () => {
  showAddUserModal.value = false;
  // cache invalidated by mutation hook
};

const handleUserUpdated = () => {
  showEditUserModal.value = false;
  // cache invalidated by mutation hook
};

const confirmDeleteUser = (userId: string, userName: string) => {
  userToDelete.value = { id: userId, name: userName };
  showDeleteModal.value = true;
};

const deleteUser = () => {
  if (!userToDelete.value) return;
  const { id, name } = userToDelete.value;
  showDeleteModal.value = false;

  adminRemoveUserFromOrg(
    { orgId: session.value.currentOrgId, userId: id },
    {
      onSuccess: () => alertStore.addSuccess("User Removed", `${name} has been removed from the organization`),
      onError: (e: any) => alertStore.addError("Error", e?.userMessage ?? "Failed to remove user"),
    }
  );

  userToDelete.value = null;
};

const handleResendInvite = (email: string) => {
  resendInvite(email, {
    onSuccess: () => alertStore.addSuccess("Invitation Resent", `Invitation resent to ${email}`),
    onError: (e: any) => alertStore.addError("Error", e?.userMessage ?? "Failed to resend invitation"),
  });
};

const handleRevokeInvite = (email: string) => {
  revokeInvite(email, {
    onSuccess: () => alertStore.addSuccess("Invitation Revoked", `Invitation for ${email} has been revoked`),
    onError: (e: any) => alertStore.addError("Error", e?.userMessage ?? "Failed to revoke invitation"),
  });
};

const tableData = computed(() => {
  return orgUsers.value.map((user: IUser) => ({
    id: user.id ?? user.email,
    firstName: user.isPending ? "(Pending)" : user.firstName,
    lastName: user.isPending ? "" : user.lastName,
    email: user.email,
    role: user.role || "N/A",
    status: user.isPending ? "Pending" : "Active",
    actions: ""
  }));
});


// Column definitions for DataView
//TODO: Add search toolbar to layout topStart
const options = {
    columns: [
      {data: 'id', title: 'ID', visible: false},
      {data: 'firstName', title: 'First Name'},
      {data: 'lastName', title: 'Last Name'},
      {data: 'email', title: 'Email'},
      {
        data: 'status',
        title: 'Status',
        orderable: false,
        searchable: false,
        render: '#status', // Use the actions slot for rendering https://datatables.net/manual/vue#Manual-slots
      },
        
      {data: 'role', title: 'Role'},
      {
        data: 'actions',
        title: 'Actions',
        orderable: false,
        searchable: false,
        render: '#actions', // Use the actions slot for rendering https://datatables.net/manual/vue#Manual-slots
      },
    ],
  } as Config;

</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard id="orgusers-card">
      <PageHeader
          title="Organisation Users"
          :breadcrumbs="[
        { label: 'Dashboard', href: '/' },
        { label: 'Organisation', href: '/org' },
        { label: 'Users'}
      ]"
      >
        <template #actions>
          <button
              v-if="canManageUsers"
              class="bb-btn-primary-cta"
              data-cy="new-project"
              @click="openInviteModal"
          >
            <i class="bi bi-plus-circle me-2"></i>Invite User
          </button>
        </template>
      </PageHeader>

      <!-- Skeleton while loading -->
      <SkeletonLoader v-if="orgUsersLoading" type="table" :columns="5" />

      <!-- Data table when loaded -->
      <DataView
          v-else
          :data="tableData"
          :view-mode="'table'"
          :options="options"
      >
        
        <template #status="props">
          <span v-if="props.rowData.status === 'Pending'" class="badge bg-warning">Invited</span>
          <span v-else class="badge bg-success">Active</span>
        </template>

        <!-- Actions column slot -->
        <template #actions="props">
          <div v-if="canManageUsers && props.rowData.status === 'Pending'" class="d-flex gap-1">
            <button
              type="button"
              class="bb-btn-secondary-cta btn-sm"
              @click="handleResendInvite(props.rowData.email)"
            >
              <i class="bi bi-envelope"></i> Resend
            </button>
            <button
              type="button"
              class="bb-btn-danger btn-sm"
              @click="handleRevokeInvite(props.rowData.email)"
            >
              <i class="bi bi-x-circle"></i> Revoke
            </button>
          </div>
          <div v-else-if="canManageUsers" class="d-flex gap-1">
            <button
              type="button"
              class="bb-btn-secondary-cta btn-sm"
              @click="openEditUserModal(props.rowData)"
            >
              <i class="bi bi-pencil"></i> Edit
            </button>
            <button
              type="button"
              class="bb-btn-danger btn-sm"
              @click="confirmDeleteUser(props.rowData.id, `${props.rowData.firstName} ${props.rowData.lastName}`)"
            >
              <i class="bi bi-trash"></i> Delete
            </button>
          </div>
        </template>

        <template #empty-state>
          <div class="text-center py-5">
            <i class="bi bi-people display-1 text-muted"></i>
            <h3 class="mt-3 text-muted">No users found</h3>
            <button v-if="canManageUsers" class="bb-btn-primary-cta mt-2" @click="openInviteModal">
              <i class="bi bi-plus-circle me-2"></i>Invite User
            </button>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>

  <InviteUserModal
    :show="showInviteModal"
    @update:show="showInviteModal = $event"
    @invited="handleUserInvited"
  />

  <AddUserModal
    v-if="canManageUsers"
    :show="showAddUserModal"
    @update:show="showAddUserModal = $event"
    @user-added="handleUserAdded"
  />

  <EditUserModal
    v-if="canManageUsers"
    :show="showEditUserModal"
    :user="selectedUser"
    @update:show="showEditUserModal = $event"
    @user-updated="handleUserUpdated"
  />

  <!-- Delete Confirmation Modal -->
  <div class="modal fade" :class="{ show: showDeleteModal }" :style="{ display: showDeleteModal ? 'block' : 'none' }" @click.self="showDeleteModal = false">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>Remove User
          </h2>
          <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to remove <strong>{{ userToDelete?.name }}</strong> from this organisation?</p>
          <p class="text-muted mb-0">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="bb-btn-cancel" @click="showDeleteModal = false">Cancel</button>
          <button class="btn btn-danger" @click="deleteUser">Remove</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showDeleteModal" class="modal-backdrop fade show" @click="showDeleteModal = false"></div>
</template>

